/**
 * extensions/dicom-rt/src/modules/dicom-measurement/src/assessments/getRecipTimepointStats.js
 *
 * RECIP 1.0 F-1 — extend output schema for PSMA+ volume mode toggle.
 *
 * F-1 changes from E-3:
 *
 *   1. summariseTp BUG FIX: non-tumour ROIs (liver-reference, blood-pool,
 *      other) are now SKIPPED when computing sumPsmaPosVolume, rather than
 *      triggering NE.
 *
 *      Root cause: computePsmaPosVolumesFromBridge sets
 *        psmaPosVolumeByTimepoint[uid] = null
 *      for non-tumour ROIs to mean "not applicable". But summariseTp was
 *      treating any null as NE, which fired immediately for every timepoint
 *      when the filteredROIContours cohort included liver-reference or
 *      blood-pool ROIs (the default 'all-rois' filter includes them).
 *
 *      Fix mirrors computePsmaVolAtTimepoint in getRecipResponse.js:
 *        only PSMA_VOL_CONTRIBUTING_TYPES (lesion, new-lesion + legacy
 *        equivalents) contribute to sumPsmaPosVolume or trigger NE.
 *        Non-tumour ROIs are silently skipped.
 *
 *   2. getChangeFromTimepoint refactored to take both cohort arrays:
 *        perTpSummations     (selected cohort)
 *        perTpCmpSummations  (compared cohort — single-lesion selection)
 *      Fixes E-3 bug where comparedSumVolume/comparedSumDiameter were
 *      incorrectly copied from the selected cohort.
 *
 *   3. New fields in every stats object:
 *        summation.comparedSumPsmaPosVolume     mm³ | null
 *        fromBaseline.comparedSumPsmaPosVolume  % | null
 *        fromNadir.comparedSumPsmaPosVolume     % | null
 *        fromLatest.comparedSumPsmaPosVolume    % | null
 *
 *   4. NE propagation rule: any null tumour ROI → timepoint NE.
 *      0 is a valid value (PSMA-cold lesion, not NE).
 *
 * Note on nadir: derived from sumVolume (CT) regardless of volumeMode.
 */

import { math } from '../index';
import { TRACKING_TYPES } from './guideline';

const { round } = math;

/**
 * Tumour ROI types that contribute to sumPsmaPosVolume.
 * Non-tumour types (liver-reference, blood-pool, other) are skipped —
 * their psmaPosVolumeByTimepoint = null means N/A, not NE.
 */
const PSMA_VOL_CONTRIBUTING_TYPES = new Set([
  TRACKING_TYPES.LESION,
  TRACKING_TYPES.NEW_LESION,
  // legacy v3/v4 equivalents
  TRACKING_TYPES.TARGET,
  TRACKING_TYPES.PSMA_NEGATIVE,
  TRACKING_TYPES.NEW_PSMA_POSITIVE,
  TRACKING_TYPES.NEW_PSMA_NEGATIVE,
  TRACKING_TYPES.NEW,
]);

/**
 * Compute sumVolume (CT), sumDiameter, sumPsmaPosVolume for one timepoint
 * and one ROI cohort.
 *
 * @returns {{ sumVolume, sumDiameter, sumPsmaPosVolume: number }}
 *   sumPsmaPosVolume: always a number ≥ 0.
 *   NE/null/calculating tumour ROIs contribute 0 (not NE) so the trend
 *   display in Findings/Analysis always has numbers to compare.
 *   Non-tumour ROIs (liver-ref, blood-pool, other) are skipped entirely.
 */
function summariseTp(timepoint, selectedROIs) {
  const ctSeriesUid = timepoint.SeriesInstanceUID;
  let sumVolume = 0;
  let sumDiameter = 0;
  let sumPsmaPosVolume = 0;

  for (const lesion of timepoint.Lesions || []) {
    const roi = selectedROIs.find(r => r.ROINumber === lesion.ROINumber);
    if (!roi) continue;

    sumVolume += lesion.volume || lesion.Volume || 0;
    sumDiameter += lesion.maxDiameterValue || 0;

    if (PSMA_VOL_CONTRIBUTING_TYPES.has(roi.trackingType)) {
      const pv = roi.psmaPosVolumeByTimepoint?.[ctSeriesUid];
      // Only add real computed values; null/undefined/calculating → 0
      if (typeof pv === 'number') sumPsmaPosVolume += pv;
    }
  }

  return { sumVolume, sumDiameter, sumPsmaPosVolume };
}

function getPercentageChange(numerator, denominator) {
  if (denominator === 0) return numerator === 0 ? 0 : Infinity;
  return 100 * (numerator / denominator);
}

/**
 * Compute % changes for both cohorts in one pass.
 *
 * @param {object} args
 * @param {Array}  args.perTpSummations      selected cohort
 * @param {Array}  args.perTpCmpSummations   compared cohort (single-lesion)
 * @param {number} args.baselineIndex
 * @param {number} args.followUpIndex
 * @param {string} args.baselineDate
 * @param {string} args.followUpDate
 */
function getChangeFromTimepoint({
  perTpSummations,
  perTpCmpSummations,
  baselineIndex,
  followUpIndex,
  baselineDate,
  followUpDate,
}) {
  if (followUpIndex <= baselineIndex) {
    return {
      initialDate: baselineDate,
      date: followUpDate,
      sumVolume: 0,
      sumDiameter: 0,
      sumPsmaPosVolume: 0,
      comparedSumVolume: 0,
      comparedSumDiameter: 0,
      comparedSumPsmaPosVolume: 0,
    };
  }

  // ── Selected cohort ──────────────────────────────────────────────────────
  const b = perTpSummations[baselineIndex];
  const f = perTpSummations[followUpIndex];

  const pcv = round(
    getPercentageChange(f.sumVolume - b.sumVolume, b.sumVolume),
    -2
  );
  const pcd = round(
    getPercentageChange(f.sumDiameter - b.sumDiameter, b.sumDiameter),
    -2
  );

  let pcPsma = null;
  if (b.sumPsmaPosVolume != null && f.sumPsmaPosVolume != null) {
    pcPsma = round(
      getPercentageChange(
        f.sumPsmaPosVolume - b.sumPsmaPosVolume,
        b.sumPsmaPosVolume
      ),
      -2
    );
  }

  // ── Compared cohort (single-lesion selection) ────────────────────────────
  const bc = perTpCmpSummations?.[baselineIndex];
  const fc = perTpCmpSummations?.[followUpIndex];

  const pcCmpV =
    bc && fc
      ? round(
          getPercentageChange(fc.sumVolume - bc.sumVolume, bc.sumVolume),
          -2
        )
      : 0;
  const pcCmpD =
    bc && fc
      ? round(
          getPercentageChange(fc.sumDiameter - bc.sumDiameter, bc.sumDiameter),
          -2
        )
      : 0;

  let pcCmpPsma = null;
  if (bc?.sumPsmaPosVolume != null && fc?.sumPsmaPosVolume != null) {
    pcCmpPsma = round(
      getPercentageChange(
        fc.sumPsmaPosVolume - bc.sumPsmaPosVolume,
        bc.sumPsmaPosVolume
      ),
      -2
    );
  }

  return {
    initialDate: baselineDate,
    date: followUpDate,
    sumVolume: pcv,
    sumDiameter: pcd,
    sumPsmaPosVolume: pcPsma,
    comparedSumVolume: pcCmpV,
    comparedSumDiameter: pcCmpD,
    comparedSumPsmaPosVolume: pcCmpPsma,
  };
}

/**
 * @param {Array}  timepoints
 * @param {Array}  selectedROIContours  cohort (may include non-tumour ROIs; they're handled)
 * @param {Array}  [comparedROIContours=[]]
 * @param {object} criteria
 */
export default function getRecipTimepointStats(
  timepoints,
  selectedROIContours,
  comparedROIContours = [],
  criteria
) {
  const selectedSummations = timepoints.map(tp =>
    summariseTp(tp, selectedROIContours)
  );
  const comparedSummations = timepoints.map(tp =>
    summariseTp(tp, comparedROIContours)
  );

  return timepoints.map((tp, seriesIndex) => {
    const sel = selectedSummations[seriesIndex];
    const cmp = comparedSummations[seriesIndex];

    const summation = {
      date: tp.SeriesDate,
      sumVolume: round(sel.sumVolume, -2),
      sumDiameter: round(sel.sumDiameter, -2),
      sumPsmaPosVolume:
        sel.sumPsmaPosVolume != null ? round(sel.sumPsmaPosVolume, -2) : null,
      comparedSumVolume: round(cmp.sumVolume, -2),
      comparedSumDiameter: round(cmp.sumDiameter, -2),
      comparedSumPsmaPosVolume:
        cmp.sumPsmaPosVolume != null ? round(cmp.sumPsmaPosVolume, -2) : null,
    };

    const baselineIndex = 0;
    const fromBaseline = getChangeFromTimepoint({
      perTpSummations: selectedSummations,
      perTpCmpSummations: comparedSummations,
      baselineIndex,
      followUpIndex: seriesIndex,
      baselineDate: timepoints[baselineIndex].SeriesDate,
      followUpDate: tp.SeriesDate,
    });

    // Nadir: minimum CT sumVolume before seriesIndex (nadir is anatomical, not PSMA-based)
    let nadirIndex = baselineIndex,
      nadirVol = Infinity;
    for (let i = 0; i < seriesIndex; i++) {
      const v = selectedSummations[i].sumVolume;
      if (v < nadirVol) {
        nadirVol = v;
        nadirIndex = i;
      }
    }
    const fromNadir = getChangeFromTimepoint({
      perTpSummations: selectedSummations,
      perTpCmpSummations: comparedSummations,
      baselineIndex: nadirIndex,
      followUpIndex: seriesIndex,
      baselineDate: timepoints[nadirIndex].SeriesDate,
      followUpDate: tp.SeriesDate,
    });

    const latestIndex = Math.max(0, seriesIndex - 1);
    const fromLatest = getChangeFromTimepoint({
      perTpSummations: selectedSummations,
      perTpCmpSummations: comparedSummations,
      baselineIndex: latestIndex,
      followUpIndex: seriesIndex,
      baselineDate: timepoints[latestIndex].SeriesDate,
      followUpDate: tp.SeriesDate,
    });

    return {
      ...tp,
      id: tp.SeriesDate,
      date: tp.SeriesDate,
      baselineDate: timepoints[baselineIndex].SeriesDate,
      nadirDate: timepoints[nadirIndex].SeriesDate,
      latestDate: timepoints[latestIndex].SeriesDate,
      existROIContours: selectedROIContours.length > 0,
      existROIs: selectedROIContours.length > 0,
      existLesions: selectedROIContours.length > 0,
      ROINames: selectedROIContours.map(({ ROIName }) => ROIName),
      comparedROINames: comparedROIContours.map(({ ROIName }) => ROIName),
      summation,
      fromBaseline,
      fromNadir,
      fromLatest,
    };
  });
}
